import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import { EntradaProdutoService } from "../service/entrada_produto_service";
import { FornecedorService } from "../service/fornecedor_service";

const CAMPO_VAZIO = "-";

/**
 * Edição de uma entrada que já compõe um lote consolidado.
 *
 * Produto e qualidade são apenas exibidos: mudá-los tiraria a entrada do lote
 * em que ela foi consolidada. Quando o fornecedor veio do Sisagri Campo, a
 * entrada foi originada por um pedido do produtor, então o fornecedor fica
 * travado e a quantidade só pode ser reduzida em relação à recebida.
 */
function ModalEditarEntrada({ open, onClose, entradaId, onSave }) {
	const [loading, setLoading] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const [erro, setErro] = useState("");
	const [entrada, setEntrada] = useState(null);
	const [fornecedores, setFornecedores] = useState([]);
	const [formData, setFormData] = useState({
		data: "",
		fornecedorId: "",
		embalagemId: "",
		codigoRastreio: "",
		notaFiscalRecibo: "",
		numeroPedido: "",
		quantidade: "",
		numeroCaixas: "",
		preco: "",
	});

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	const carregar = useCallback(async () => {
		setLoading(true);
		setErro("");
		try {
			const entradaProdutoService = new EntradaProdutoService();
			const dados = await entradaProdutoService.getById(entradaId);

			setEntrada(dados);
			setFormData({
				data: dados.data ? dados.data.split("T")[0] : "",
				fornecedorId: dados.fornecedorId || "",
				embalagemId: dados.embalagemId || "",
				codigoRastreio: dados.codigoRastreio || "",
				notaFiscalRecibo: dados.notaFiscalRecibo || "",
				numeroPedido: dados.numeroPedido || "",
				quantidade: dados.quantidade ?? "",
				numeroCaixas: dados.numeroCaixas ?? "",
				preco: dados.preco ?? "",
			});

			// Fornecedor travado (entrada vinda do Sisagri Campo) não precisa da
			// lista completa.
			if (dados.quantidadeOriginal !== null && dados.quantidadeOriginal !== undefined) {
				setFornecedores(dados.fornecedor ? [dados.fornecedor] : []);
				return;
			}

			const cultivoId = dados.produtoUnidadeDistribuidora?.cultivoId;
			try {
				const lista = await entradaProdutoService.getFornecedoresPorProduto(
					unidadeDistribuidoraId,
					cultivoId,
				);
				setFornecedores(lista || []);
			} catch (error) {
				console.error("Erro ao buscar fornecedores do produto:", error);
				const fornecedorService = new FornecedorService();
				const todos = await fornecedorService.getAll(unidadeDistribuidoraId, 1, 1000);
				setFornecedores(todos?.data || []);
			}
		} catch (error) {
			console.error("Erro ao carregar entrada:", error);
			setErro(error.message || "Erro ao carregar entrada");
		} finally {
			setLoading(false);
		}
	}, [entradaId, unidadeDistribuidoraId]);

	useEffect(() => {
		if (open && entradaId) {
			carregar();
			return;
		}

		// Fechado, descarta o que estava em tela: sem isso a próxima abertura
		// mostra por um instante os dados da entrada anterior.
		setEntrada(null);
		setErro("");
	}, [open, entradaId, carregar]);

	const produto = entrada?.produtoUnidadeDistribuidora;
	const embalagens = produto?.embalagens || [];
	const embalagemSelecionada = embalagens.find((e) => e.id === formData.embalagemId);
	// A unidade de medida faz parte da identidade do lote consolidado, então a
	// entrada só pode trocar de embalagem dentro da mesma unidade.
	const unidadeMedidaId = embalagemSelecionada?.unidadeMedidaId || entrada?.unidadeMedidaId || "";
	const unidadeSigla =
		embalagemSelecionada?.unidadeMedida?.sigla || entrada?.unidadeMedida?.sigla || "";
	const qualidades = entrada?.qualidades || [];
	const totalClassificado = qualidades.reduce((soma, q) => soma + (q.quantidade || 0), 0);
	// `quantidadeOriginal` só é preenchida na entrada gerada por pedido do
	// Sisagri Campo — é ela que marca a origem, e não o fornecedor: cadastro
	// manual para um fornecedor espelhado do Campo segue totalmente editável.
	// O teto também vem daí, e não da última quantidade salva, para que corrigir
	// para menos não impeça de voltar ao valor original.
	const quantidadeRecebida = entrada?.quantidadeOriginal;
	const origemSisagriCampo = quantidadeRecebida !== null && quantidadeRecebida !== undefined;

	const nomeProduto = produto?.cultivo
		? `${produto.cultivo.cultura || ""}${
				produto.cultivo.variedade ? ` - ${produto.cultivo.variedade}` : ""
		  }`
		: CAMPO_VAZIO;

	const recalcularCaixas = (quantidade, embalagemId) => {
		const embalagem = embalagens.find((e) => e.id === embalagemId);
		const qtd = parseFloat(quantidade) || 0;
		return embalagem?.quantidade > 0 && qtd > 0
			? String(Math.ceil(qtd / embalagem.quantidade))
			: "";
	};

	const handleQuantidadeChange = (valor) => {
		setFormData((atual) => ({
			...atual,
			quantidade: valor,
			numeroCaixas: recalcularCaixas(valor, atual.embalagemId),
		}));
	};

	const handleEmbalagemChange = (embalagemId) => {
		setFormData((atual) => ({
			...atual,
			embalagemId,
			numeroCaixas: recalcularCaixas(atual.quantidade, embalagemId),
		}));
	};

	const validar = () => {
		if (!formData.data) return "Informe a data";
		if (!formData.fornecedorId) return "Selecione um fornecedor";

		const quantidade = parseFloat(formData.quantidade);
		if (!quantidade || quantidade <= 0) return "Informe a quantidade";

		if (origemSisagriCampo && quantidade > quantidadeRecebida) {
			return `A quantidade não pode ser maior que a recebida do Sisagri Campo (${quantidadeRecebida}${
				unidadeSigla ? ` ${unidadeSigla}` : ""
			})`;
		}

		if (totalClassificado > quantidade) {
			return `A quantidade não pode ser menor que o total já classificado por qualidade (${totalClassificado})`;
		}

		const preco = parseFloat(formData.preco);
		if (!preco || preco <= 0) return "Informe o preço";

		if (!unidadeMedidaId) return "A embalagem selecionada não possui unidade de medida";

		return "";
	};

	const handleSalvar = async () => {
		const mensagem = validar();
		if (mensagem) {
			setErro(mensagem);
			return;
		}

		setErro("");
		setSalvando(true);
		try {
			const entradaProdutoService = new EntradaProdutoService();

			await entradaProdutoService.update({
				id: entrada.id,
				unidadeDistribuidoraId: entrada.unidadeDistribuidoraId,
				// Produto e qualidades seguem como estão: o update do servidor
				// regrava esses vínculos a partir do que for enviado.
				produtoUnidadeDistribuidoraId: entrada.produtoUnidadeDistribuidoraId,
				unidadeMedidaId,
				embalagemId: formData.embalagemId || undefined,
				data: formData.data,
				fornecedorId: formData.fornecedorId,
				// null (e não undefined) para que apagar um campo opcional na
				// edição realmente limpe o valor gravado.
				codigoRastreio: formData.codigoRastreio || null,
				notaFiscalRecibo: formData.notaFiscalRecibo || null,
				numeroPedido: formData.numeroPedido || null,
				quantidade: parseFloat(formData.quantidade),
				numeroCaixas: formData.numeroCaixas ? parseInt(formData.numeroCaixas, 10) : null,
				preco: parseFloat(formData.preco),
				recebimentoConfirmado: entrada.recebimentoConfirmado,
				qualidades: qualidades.length
					? qualidades.map((q) => ({
							qualidadeColheitaId: q.qualidadeColheitaId,
							quantidade: q.quantidade,
					  }))
					: undefined,
			});

			if (onSave) {
				await onSave();
			}
			onClose();
		} catch (error) {
			console.error("Erro ao salvar entrada:", error);
			setErro(error.message || "Erro ao salvar entrada");
		} finally {
			setSalvando(false);
		}
	};

	const campoSomenteLeitura = (rotulo, valor) => (
		<Box sx={{ flex: 1 }}>
			<Typography
				variant="caption"
				sx={{ color: "#666", display: "block", textTransform: "uppercase", fontWeight: 600 }}
			>
				{rotulo}
			</Typography>
			<Typography variant="body1" sx={{ color: "#333", fontWeight: 500 }}>
				{valor}
			</Typography>
		</Box>
	);

	return (
		<Dialog open={open} onClose={salvando ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ bgcolor: "#1a9e8f", color: "white", fontSize: "18px", fontWeight: 600 }}>
				Editar Entrada
			</DialogTitle>
			<DialogContent sx={{ mt: 3, px: 3 }}>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress sx={{ color: "#1a9e8f" }} />
					</Box>
				) : (
					<>
						{erro && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{erro}
							</Alert>
						)}

						{entrada && (
							<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
								{/* Campos que não podem mudar depois da consolidação */}
								<Box
									sx={{
										display: "flex",
										gap: 3,
										p: 2,
										bgcolor: "#f9f9f9",
										borderRadius: 1,
									}}
								>
									{campoSomenteLeitura("Produto", nomeProduto)}
									{campoSomenteLeitura(
										"Qualidade",
										qualidades.length
											? qualidades
													.map(
														(q) =>
															`${q.qualidadeColheita?.nome || "Sem qualidade"}: ${q.quantidade}`,
													)
													.join(" · ")
											: "Sem qualidade",
									)}
								</Box>

								{origemSisagriCampo && (
									<Alert severity="info">
										Entrada originada no Sisagri Campo: o fornecedor não pode ser alterado e a
										quantidade não pode passar de {quantidadeRecebida}
										{unidadeSigla ? ` ${unidadeSigla}` : ""}.
									</Alert>
								)}

								<Box sx={{ display: "flex", gap: 2 }}>
									<TextField
										sx={{ flex: 1 }}
										required
										label="Data"
										type="date"
										value={formData.data}
										onChange={(e) => setFormData({ ...formData, data: e.target.value })}
										InputLabelProps={{ shrink: true }}
									/>

									<FormControl sx={{ flex: 2 }} required>
										<InputLabel>Fornecedor</InputLabel>
										<Select
											value={formData.fornecedorId}
											onChange={(e) =>
												setFormData({ ...formData, fornecedorId: e.target.value })
											}
											label="Fornecedor"
											disabled={origemSisagriCampo}
										>
											{fornecedores.map((fornecedor) => (
												<MenuItem key={fornecedor.id} value={fornecedor.id}>
													{fornecedor.nome}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>

								<Box sx={{ display: "flex", gap: 2 }}>
									<FormControl sx={{ flex: 2 }}>
										<InputLabel>Embalagem</InputLabel>
										<Select
											value={formData.embalagemId}
											onChange={(e) => handleEmbalagemChange(e.target.value)}
											label="Embalagem"
										>
											{embalagens.map((embalagem) => (
												<MenuItem
													key={embalagem.id}
													value={embalagem.id}
													disabled={
														!!entrada.unidadeMedidaId &&
														embalagem.unidadeMedidaId !== entrada.unidadeMedidaId
													}
												>
													{embalagem.descricao}
													{embalagem.quantidade > 0
														? ` (${embalagem.quantidade}${
																embalagem.unidadeMedida?.sigla || ""
														  })`
														: embalagem.unidadeMedida
														? ` (${embalagem.unidadeMedida.sigla})`
														: ""}
												</MenuItem>
											))}
										</Select>
									</FormControl>

									<TextField
										sx={{ flex: 1 }}
										required
										label={`Quantidade${unidadeSigla ? ` (${unidadeSigla})` : ""}`}
										type="number"
										value={formData.quantidade}
										onChange={(e) => handleQuantidadeChange(e.target.value)}
										inputProps={{
											min: 0,
											step: 0.01,
											max: origemSisagriCampo ? quantidadeRecebida : undefined,
										}}
									/>

									<TextField
										label="Número de Embalagens"
										type="number"
										value={formData.numeroCaixas}
										InputLabelProps={{ shrink: !!formData.numeroCaixas }}
										inputProps={{ readOnly: true, style: { color: "#555" } }}
										sx={{ flex: 1, "& .MuiOutlinedInput-root": { bgcolor: "#f5f5f5" } }}
									/>

									<TextField
										sx={{ flex: 1 }}
										required
										label={`Preço por ${unidadeSigla || "unidade"} (R$)`}
										type="number"
										value={formData.preco}
										onChange={(e) => setFormData({ ...formData, preco: e.target.value })}
										inputProps={{ min: 0, step: 0.01 }}
									/>
								</Box>

								<Box sx={{ display: "flex", gap: 2 }}>
									<TextField
										sx={{ flex: 1 }}
										label="Código de Rastreio"
										value={formData.codigoRastreio}
										onChange={(e) =>
											setFormData({ ...formData, codigoRastreio: e.target.value })
										}
									/>
									<TextField
										sx={{ flex: 1 }}
										label="Nota Fiscal / Recibo"
										value={formData.notaFiscalRecibo}
										onChange={(e) =>
											setFormData({ ...formData, notaFiscalRecibo: e.target.value })
										}
									/>
									<TextField
										sx={{ flex: 1 }}
										label="Nº Pedido"
										value={formData.numeroPedido}
										onChange={(e) => setFormData({ ...formData, numeroPedido: e.target.value })}
									/>
								</Box>
							</Box>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} disabled={salvando} sx={{ textTransform: "none", color: "#666" }}>
					Cancelar
				</Button>
				<Button
					onClick={handleSalvar}
					variant="contained"
					disabled={loading || salvando || !entrada}
					sx={{
						bgcolor: "#1a9e8f",
						"&:hover": { bgcolor: "#138a7d" },
						textTransform: "none",
						px: 4,
					}}
				>
					{salvando ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Salvar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ModalEditarEntrada;
